<template>
  <teleport to="body" id="agEditPanel">
    <div v-if="modelValue">
      <div class="edit-panel-overlay" @click="onCancel"></div>
      <div class="edit-panel-container">
        <div class="border-bottom">
          <div class="d-flex justify-content-between align-items-center  p-3">
            <div class="d-flex align-items-center gap-2">
              <i v-if="canGoBack" class="pi pi-chevron-left" style="cursor: pointer;" @click="$emit('back')"></i>
              <h5 class="m-0 panel-title">{{ title }}</h5>
            </div>
            <Button class="btn-icon" icon="pi pi-times" variant="text" rounded aria-label="Cancel" @click="onCancel"
              size="normal" />
          </div>
          <ToggleSwitch v-if="showEnabled" v-model="formData.enabled" :label="$t('enabled')" :show-info="true"
            :tooltip="formType === 'appGroup' ? tooltips.appGroup.enableDisable : tooltips.shared.enableDisable" />
        </div>

        <div :class="formType !== 'fileAccess' ? 'overflow-auto' : ''" style="max-height: calc(100% - 120px);">
          <component :is="formComponent" v-model:form="formData" :row="row" :formType="formType" :enumKey="enumKey"
            @edit="onChildEdit" @validation="onValidation" :linked-app="linkedApp" />
        </div>

        <div v-if="canGoBack && formType === 'fileTypePolicy'" class="panel-warning">
          <i class="pi pi-exclamation-triangle mr-2"></i>
          These settings override any other settings in AppGroup rules.
        </div>

        <div class="border-top p-3 d-flex justify-content-end gap-2 buttons-div">
          <button class="btn" style="border: 1px solid #D2D9E2;" @click="onCancel">Cancel</button>
          <button class="btn success-btn" @click="onSave">Save</button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, onBeforeUnmount ,computed} from 'vue'
import Button from "primevue/button";
import ToggleSwitch from './forms/components/toggle-switch.vue'
import tooltips from '@/assets/tooltips/en.json'
import { useStore } from "@/store";
import { AppGroupTypes } from '@/helpers/enums/AppGroupTypes';
const store = useStore();

const props = defineProps({
  modelValue: Boolean,
  title: {
    type: String,
    default: 'Edit Panel'
  },
  formComponent: {
    type: [Object, Function],
    required: true
  },
  form: {
    type: Object,
    default: () => ({
    })
  },
  enumKey: { type: String, default: 'AccessControl' },
  formType: { type: String, default: '' },
  linkedApp: { type: Boolean, default: false },
  canGoBack: { type: Boolean, default: false },
  showEnabled: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:modelValue', 'save', 'cancel', 'edit', 'back'])
const formData = ref({ ...props.form })
const appGroupInfo = computed(() => store.state.general.appGroupInfo || {});
watch(() => props.form, (newVal) => {
  const incoming = { ...(newVal || {}) };
  const defaults = {
    enabled: true,
    aecSecurityAllowRun: true,//process
    moduleSecurityAllowRun: true,
    accessControl: (Number(appGroupInfo.value?.appGroupType) === AppGroupTypes.PowerApps || props.formType === 'process') ? 2 : 1,
    // aecConditionZoneIdentifier: 1,
    // moduleConditionZoneIdentifier: 1,
    aecSecurityChildProcess: appGroupInfo.value.childProcess ? 2 : 4,
    moduleSecurityChildProcess: appGroupInfo.value.childProcess ? 2 : 4,
    registryHive: 2,//registry access
    appliesTo: 1,
    allowToRun: true,//code load
    // zoneIdentifier:1,
    memorySecurity: 2,
    inheritance: 1,
    moduleControl: false,
    applicationExecutionControl: true,
    aecCodeLoadSecurity:(Number(appGroupInfo.value?.appGroupType)) === AppGroupTypes.PowerApps ? 2 : 1,
    childProcess:true
  };
  const merged = { ...defaults, ...incoming };
  const isNew = (incoming.id ?? 0) === 0;
  merged.memorySecurity = isNew
    ? ((incoming.memorySecurity ?? 0) === 0 ? defaults.memorySecurity : incoming.memorySecurity)
    : (incoming.memorySecurity ?? defaults.memorySecurity);
  merged.inheritance = isNew
    ? ((incoming.inheritance ?? 0) === 0 ? defaults.inheritance : incoming.inheritance)
    : (incoming.inheritance ?? defaults.inheritance);
  formData.value = merged;
}, { deep: true, immediate: true })

function lockBodyScroll() {
  try {
    const body = document.body
    const html = document.documentElement
    body.dataset.prevOverflow = body.style.overflow || ''
    html.dataset.prevOverflow = html.style.overflow || ''
    body.style.overflow = 'hidden'
    html.style.overflow = 'hidden'
  } catch (e) { /* no-op */ }
}

function unlockBodyScroll() {
  try {
    const body = document.body
    const html = document.documentElement
    if (body.dataset.prevOverflow !== undefined) {
      body.style.overflow = body.dataset.prevOverflow
      delete body.dataset.prevOverflow
    } else {
      body.style.overflow = ''
    }
    if (html.dataset.prevOverflow !== undefined) {
      html.style.overflow = html.dataset.prevOverflow
      delete html.dataset.prevOverflow
    } else {
      html.style.overflow = ''
    }
  } catch (e) { /* no-op */ }
}

watch(() => props.modelValue, (open) => {
  if (open) lockBodyScroll(); else unlockBodyScroll();
}, { immediate: true })

function onSave() {
  emit('save', formData.value)
}

function onCancel() {
  if (props.canGoBack && props.formType === 'fileTypePolicy') {
    emit('back')
    return
  }
  emit('cancel')
  emit('update:modelValue', false)
  unlockBodyScroll()
}

function onChildEdit(payload) {
  emit('edit', payload)
}
onBeforeUnmount(() => {
  unlockBodyScroll()
})
</script>


<style scoped>
.edit-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1040;
}

.edit-panel-container {
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: 550px;
  background-color: #fff;
  z-index: 1050;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.2);
  animation: slideInRight 0.3s ease-out;
  overflow-y: auto;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(0);
  }
}

.btn-icon {
  background: transparent;
  border: none;
  padding: 4px;
  color: #000;
  border-radius: 50%;
  box-shadow: none;
}

.btn-icon:hover {
  background-color: #e6f0fb;
}

.success-btn {
  background-color: #003060;
  color: #fff;
}

.buttons-div button {
  width: 30%;
}

.panel-title {
  color: #164B6C;
  font-weight: bold;
}

.border-bottom {
  padding-bottom: 10px;
  margin-bottom: 10px;
}

.panel-warning {
  background: #fac812;
  color: #000;
  border-top: 1px solid #FFE082;
  border-bottom: 1px solid #FFE082;
  padding: 10px 14px;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 1rem;
  margin-right: 2rem;
  justify-content: center;
  text-align: center;

}

.buttons-div .btn {
  transition: background 120ms ease, color 120ms ease, transform 80ms ease, box-shadow 120ms ease, filter 120ms ease;
}

.buttons-div .btn:not(.success-btn):hover {
  background-color: #f8f8f8;
  color: #5e5e5e;
}

.buttons-div .btn:not(.success-btn):active {
  transform: translateY(1px);
  box-shadow: none;
}

.buttons-div .success-btn:hover {
  filter: brightness(1.08);
  box-shadow: 0 6px 14px rgba(22, 75, 108, 0.35);
}

.buttons-div .success-btn:active {
  filter: brightness(0.92);
  transform: translateY(1px);
  box-shadow: 0 2px 6px rgba(22, 75, 108, 0.2);
}
</style>
